<template>
  <div class="video-carousel">
    <div class="flex gap-[16px] mb-[40px] pc-only">
      <div
        v-for="(item, index) in infos"
        :key="item.title"
        :style="{
          '--data-percent': activeIndex === index ? 1 - progress : 1,
        }"
        :class="{ active: activeIndex === index }"
        class="video-carousel-tab-item"
        @click="goTo(index)"
      >
        {{ item.title }}
      </div>
    </div>
    <div class="m-only video-infos_tabs" ref="tabsRef">
      <div
        v-for="(item, index) in infos"
        :key="item.title"
        :style="{
          '--data-percent': activeIndex === index ? 1 - progress : 1,
        }"
        :class="{ active: activeIndex === index }"
        class="video-carousel-tab-item"
        @click="goTo(index)"
      >
        {{ item.title }}
      </div>
    </div>
    <div class="video-container-wrapper rounded-[12px] overflow-hidden">
      <VideoPlayer
        ref="videoRef"
        :key="activeIndex"
        :class="`w-full h-full video_cover`"
        :url="src"
        :poster="poster"
        :autoplay="true"
        :loop="false"
        :muted="isMuted"
        :enable-fullscreen="false"
        loading="lazy"
        @ended="next"
        @time-update="timeUpdate"
        :title="infos[activeIndex].videoDesc"
        :controls="showControls"
      />
    </div>
    <div class="dots">
      <Dot
        v-for="(item, index) in infos"
        :key="item.title"
        :active="activeIndex === index"
        @click="goTo(index)"
      />
    </div>
    <div class="text-content" v-if="infos[activeIndex].desc">
      <h3 v-if="infos[activeIndex].title">{{ infos[activeIndex].subTitle }}</h3>
      <p>{{ infos[activeIndex].desc }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import VideoPlayer from '~/components/video-player/index.vue';
import Dot from '~/components/xDot/index.vue';

const props = withDefaults(
  defineProps<{
    infos: {
      title: string;
      url: string;
      poster: string;
      desc?: string;
      subTitle?: string;
      videoDesc?: string;
    }[];
    showControls?: boolean;
    muted?: boolean;
  }>(),
  {
    showControls: true,
  },
);

const tabsRef = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
const videoRef = ref();
const src = ref(props.infos[activeIndex.value].url);
const poster = ref(props.infos[activeIndex.value].poster);

const progress = ref(0);

const injectedMuted = inject<Ref<boolean> | boolean>('isMuted', true);
const isMuted = computed(() => {
  if (isRef(injectedMuted)) {
    return Boolean(injectedMuted.value);
  }
  return Boolean(injectedMuted);
});

const goTo = (index: number) => {
  activeIndex.value = index;
  scrollTabIntoView(index);
};

watch(
  () => activeIndex.value,
  val => {
    src.value = props.infos[val].url;
    poster.value = props.infos[val].poster;
  },
);

function next() {
  if (activeIndex.value === props.infos.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
  scrollTabIntoView(activeIndex.value);
}

// 仅在横向 tab 容器内滚动，避免 scrollIntoView 带动页面/外层横向滚动导致布局错位
const scrollTabIntoView = (tabIndex: number) => {
  const container = tabsRef.value;
  if (!container || container.clientWidth === 0) return;

  const tabElements = container.querySelectorAll('.video-carousel-tab-item');
  const targetTab = tabElements[tabIndex] as HTMLElement | undefined;
  if (!targetTab) return;

  const left = targetTab.offsetLeft - (container.clientWidth - targetTab.offsetWidth) / 2;
  const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);

  container.scrollTo({
    left: Math.max(0, Math.min(left, maxScroll)),
    behavior: 'smooth',
  });
};

function timeUpdate(currentTime: number) {
  const duration = videoRef.value?.duration?.();

  if (typeof duration === 'number' && !isNaN(duration) && duration > 0) {
    const newProgress = currentTime / duration;
    // 确保在 0-1 范围内
    progress.value = Math.max(0, Math.min(1, newProgress));
  }
  // 不再有 else 分支，保持之前的值
}
</script>

<style lang="scss" scoped src="./styles/video-carousel-pc.scss"></style>
<style lang="scss" scoped src="./styles/video-carousel-mobile.scss"></style>
